/**
 * useContactInfo — small React hook that reads support / privacy /
 * business-name fields from the public website config so every page
 * on the marketing site (Support, Privacy, Terms, Refund, Landing,
 * Activation) surfaces a single admin-controllable set of contact
 * addresses.
 *
 * Change once in Admin → Settings → Website, and every mailto link,
 * footer line, FAQ answer and PDF placeholder updates on the next
 * page load.
 *
 * Cached in localStorage (`zapp_contact_info`) so the pages render
 * with the last-known good values on first paint instead of the
 * hardcoded defaults, then reconcile once the fetch resolves.
 */
import { useEffect, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "zapp_contact_info";

const DEFAULTS = {
  contactEmail: "support@zaplayerpro.club",
  privacyEmail: "privacy@zaplayerpro.club",
  siteName: "ZA Player Pro",
  siteUrl: "https://zaplayerpro.club",
};

function _readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function _writeCache(value) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(value)); } catch { /* noop */ }
}

export default function useContactInfo() {
  const [info, setInfo] = useState(() => _readCache() || DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/public/website-config`);
        if (!r.ok) return;
        const cfg = await r.json();
        if (cancelled) return;
        // Derive privacy@ from contact@ when the admin hasn't set it
        // explicitly — same TLD, same style as vuplayer.pro / smarters.
        const contactEmail = (cfg?.contact_email || DEFAULTS.contactEmail).trim();
        const explicitPrivacy = (cfg?.privacy_email || "").trim();
        const privacyEmail =
          explicitPrivacy
          || (contactEmail.includes("@")
                ? `privacy@${contactEmail.split("@").pop()}`
                : DEFAULTS.privacyEmail);
        const resolved = {
          contactEmail,
          privacyEmail,
          siteName: (cfg?.brand_name || DEFAULTS.siteName).trim(),
          siteUrl: (cfg?.site_url || DEFAULTS.siteUrl).trim(),
        };
        setInfo(resolved);
        _writeCache(resolved);
      } catch { /* offline / server down — keep defaults or cache */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return info;
}
